import { dateTimeField } from '@lib/form';
import PropTypes from 'prop-types';
import DatePickerProvider from '@components/atoms/DatePickerProvider';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import FieldLabel from '../FieldLabel';
import getFieldStatus from '../fieldStatus';

const PICKERS = {
  time: TimePicker,
  date: DatePicker,
  datetime: DateTimePicker,
};

export default function DateTimeField(props) {
  const { type } = props;
  const Picker = PICKERS[type];

  if (!Picker) {
    throw new Error(`Type '${type}' not supported.`);
  }

  return (
    <DatePickerProvider>
      <PickerField {...props} picker={Picker} />
    </DatePickerProvider>
  );
}

DateTimeField.propTypes = {
  type: PropTypes.oneOf(['date', 'datetime', 'time']).isRequired,
};

/**
 * All three pickers render the same MUI text field underneath and differ only in which
 * component they mount, so they share one wrapper rather than three near-identical ones.
 *
 * The control height, radius and always-shrunk label come from the form theme —
 * `size="small"` used to pin them to 40px and fight it. The old props also passed
 * `shrink: true` here, which the text field does not read; the label only ever shrank
 * because a picker always reports a value.
 */
function PickerField(props) {
  const {
    picker: Picker,
    name,
    disabled,
    value = '',
    error,
    touched,
    label = '',
    isRequired,
    helperText = '',
    handleChange,
    handleBlur,
    readOnly,
  } = props;
  const { hasError, message } = getFieldStatus({ error, touched, helperText });

  return (
    <Picker
      disabled={disabled}
      name={name}
      onChange={handleChange}
      slotProps={{
        textField: {
          fullWidth: true,
          error: hasError,
          helperText: message,
          label: <FieldLabel isRequired={isRequired} label={label} />,
          readOnly,
          onBlur: handleBlur,
          // `aria-required` rather than MUI's `required`, which would print an asterisk.
          inputProps: { 'aria-required': isRequired || undefined },
        },
      }}
      value={value}
    />
  );
}

PickerField.propTypes = {
  picker: PropTypes.elementType.isRequired,
  name: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  value: PropTypes.string,
  error: PropTypes.string,
  touched: PropTypes.bool,
  label: PropTypes.string,
  isRequired: PropTypes.bool,
  helperText: PropTypes.string,
  handleChange: PropTypes.func.isRequired,
  handleBlur: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
};

DateTimeField.getDefaultValue = dateTimeField.getDefaultValue;
DateTimeField.serializeValue = dateTimeField.serializeValue;
DateTimeField.deserializeValue = dateTimeField.deserializeValue;
DateTimeField.getDisplayValue = dateTimeField.getDisplayValue;
DateTimeField.validationSchema = dateTimeField.validationSchema;
DateTimeField.graphQlSchema = `
typeDateTime {
  id
  type
}
`;
